import { SalesTaxFiling, TransactionStatus } from '@commenda/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Separator,
} from '@commenda/kakashi';
import { Country } from '@prisma/client';
import { State } from 'country-state-city';
import { Filing, FilingFrequencyNameMap } from '../../../molecules';
import {
  SalesTaxDisableFilterMap,
  SalesTaxFiltersType,
} from '../TransactionFilters/config';
import TransactionsTableForModal from './TransactionsTableForModal';

interface ViewTransactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  corporationId: number;
  filing: Filing<SalesTaxFiling>;
  companyId: number;
  salesTaxTestMode?: boolean;
}

type InternalFilters = {
  from_date?: string;
  to_date?: string;
  reporting_date_override_from_date?: string;
  reporting_date_override_to_date?: string;
  transaction_source?: string;
  currency?: string[];
  destination_states?: string[];
  statuses?: TransactionStatus[];
};

export const ViewTransactionsModal = ({
  isOpen,
  onClose,
  corporationId,
  filing,
  companyId,
  salesTaxTestMode = false,
}: ViewTransactionsModalProps) => {
  let filingJurisdiction = filing?.jurisdiction;
  if (filing?.jurisdiction) {
    filingJurisdiction = `US_${filing?.jurisdiction}`;
  }

  // Get state name from jurisdiction code
  const stateName = filing?.jurisdiction
    ? State.getStateByCodeAndCountry(filing.jurisdiction, Country.US)?.name ||
      filing.jurisdiction
    : '';

  const initialFilters: InternalFilters = {
    reporting_date_override_from_date:
      filing?.metadata?.reporting_period_start_date,
    reporting_date_override_to_date:
      filing?.metadata?.reporting_period_end_date,
    destination_states: filingJurisdiction ? [filingJurisdiction] : undefined,
    // Show all statuses except VOIDED
    statuses: [
      TransactionStatus.LOCKED,
      TransactionStatus.UNCOMMITTED,
      TransactionStatus.COMMITTED,
    ],
  };

  const disableFilters: SalesTaxDisableFilterMap = {
    [SalesTaxFiltersType.PERIOD]: true,
    [SalesTaxFiltersType.DESTINATION_COUNTRIES]: true,
    [SalesTaxFiltersType.DESTINATION_STATES]: true,
  };

  const companyIds = process.env.NEXT_PUBLIC_EU_COMPANY_IDS || "";
  const isEuTaxCountry = companyIds.split(",").includes(companyId?.toString() || "");

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="min-w-[95vw] max-w-[95vw] min-h-[95vh] max-h-[95vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-lg">
              <span className="txt-large-bold text-text-primary">
                Viewing transactions for {stateName} for{' '}
                {FilingFrequencyNameMap[filing.frequency]} filing
              </span>
              <Separator orientation="vertical" className="h-6" />
              <span className="txt-small-medium text-text-quaternary">
                {filing.filingPeriod}
              </span>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden flex flex-col">
          <TransactionsTableForModal
            corporationId={corporationId}
            companyId={companyId}
            initialFilters={initialFilters}
            disableFilters={disableFilters}
            tableContainerClassName="max-h-[calc(95vh-500px)] overflow-y-auto"
            tableWrapperClassName="h-full flex flex-col"
            salesTaxTestMode={salesTaxTestMode}
            isEuUploadFlow={isEuTaxCountry}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
